import React, { useState, useEffect } from 'react';
import InputWithClear from './../InputComponents/InputWithClear';
import ShownPanelsEnum from '../../Enums/ShownPanelsEnum';
import selectHelper from '../../Helpers/selectHelper';
import InputTypesEnum from '../../Enums/InputTypesEnum';
import urlHelper from '../../Helpers/urlHelper';
import SavedFilterWindow from './SavedFilterWindow';

export default function GeneralSearchPanel(props) {
  const [searchItem, setSearchItem] = useState(() => ({ ...props.searchItem }));
  const [filterCleared, setFilterCleared] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openSavePanel, setOpenSavePanel] = useState(null);

  const sp = props.searchparamsType;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && props.clickOnCancel) props.clickOnCancel();
    };
    document.addEventListener('keydown', onKey, false);
    return () => document.removeEventListener('keydown', onKey, false);
  }, [props.clickOnCancel]);

  // Lets the caller follow the selection while it is being made, e.g. to narrow the other lists by it. Nothing is
  // applied before Search.
  useEffect(() => {
    if (props.changeOfSearchItemFunction) props.changeOfSearchItemFunction(searchItem);
  }, [searchItem]);

  const changeOfEditedMultipItem = (inputData) => {
    setSearchItem((prev) => {
      const tempSearchItem = { ...prev };
      inputData.map((c) => (tempSearchItem[c.fieldName] = c.newValue));
      return tempSearchItem;
    });
  };

  const changeOfSearchItem = (fieldName, newValue) => {
    setSearchItem((prev) => {
      let newSearchItem = { ...prev, [fieldName]: newValue };
      if (sp.valuesToEraseOnChange && sp.valuesToEraseOnChange.values.some((s) => s.triggerField === fieldName)) {
        const changeItem = sp.valuesToEraseOnChange.values.find((s) => s.triggerField === fieldName);
        newSearchItem = { ...prev, [fieldName]: newValue, [changeItem.toErase]: changeItem.value };
      }
      return newSearchItem;
    });
  };

  const renderInputField = (f) => {
    let type = f.type;
    let labelText = f.label;
    if (f.hide && (f.hide === true || (typeof f.hide === 'function' && f.hide(searchItem)))) {
      return null;
    }
    if (f.showCondition && !f.showCondition(searchItem)) {
      type = InputTypesEnum.PlaceHolder;
      labelText = '';
    }
    return (
      <div
        className={f.className}
        key={`keydiv${f.fieldName}`}
      >
        <InputWithClear
          fieldName={f.fieldName}
          labelText={labelText}
          type={type}
          value={searchItem[f.fieldName]}
          selectValues={f.options}
          onChange={(fieldName, newValue) => {
            changeOfSearchItem(fieldName, newValue);
            setFilterCleared(false);
          }}
          key={`key${f.fieldName}`}
          roleKey={f.roleKey}
          tooltipText={f.tooltipText}
          disabled={f.disabled}
          editedItem={searchItem}
          idFilter={f.idFilter && f.idFilter(searchItem)}
          filterCleared={filterCleared}
          IsCheckBoxEnabled={f.IsCheckBoxEnabled}
          changeOfEditedMultipItem={(data) => changeOfEditedMultipItem(data)}
          validityChecker={f.validityChecker}
        />
      </div>
    );
  };

  const handleSaveFilterOnClick = () => {
    setOpenSavePanel(
      <SavedFilterWindow
        mode='save'
        itemToSave={searchItem}
        saveType={getSaveType()}
        onClose={() => setOpenSavePanel(null)}
      />
    );
  };

  const clickOnOpenFileInOpenWindow = (item) => {
    setOpenSavePanel(null);
    setSearchItem(item);
  };

  const handleOpenFilterOnClick = () => {
    setOpenSavePanel(
      <SavedFilterWindow
        mode='load'
        saveType={getSaveType()}
        onClose={() => setOpenSavePanel(null)}
        onLoad={clickOnOpenFileInOpenWindow}
      />
    );
  };

  const validityCheckerForTheEntireForm = () => {
    for (const field of sp.fieldsInSearchBox) {
      const validation = field.validityChecker && field.validityChecker(searchItem[field.fieldName], searchItem);
      if (validation !== '' && validation !== undefined) return false;
    }
    return true;
  };

  // Persistence key for saved searches. saveType is a MANDATORY static on every
  // search-params class (see SearchParamsBase); fail fast if it is missing rather than
  // silently reading the minifiable class name.
  const getSaveType = () => {
    const saveType = sp.saveType;
    if (!saveType) {
      return 'Uncategorized';
    }
    return saveType;
  };

  const handleClearAll = () => {
    // Some callers keep specific filters when clearing (e.g. CX client id) — defer to them.
    if (props.clickOnClearAll) {
      props.clickOnClearAll();
      return;
    }
    urlHelper.removeSearchParamsFromUrl();
    const newSearchItem = new sp();
    setSearchItem(newSearchItem);
    setFilterCleared(true);
  };

  const renderButtons = () => {
    const formValid = validityCheckerForTheEntireForm();
    return (
      <footer>
        <button
          type='button'
          className='icon-btn ff-preset'
          aria-label='Save filter'
          title='Save filter'
          onClick={handleSaveFilterOnClick}
        >
          <i className='fa-solid fa-save' />
        </button>
        <button
          type='button'
          className='icon-btn ff-preset'
          aria-label='Load filter'
          title='Load filter'
          onClick={handleOpenFilterOnClick}
        >
          <i className='fa-solid fa-folder-open' />
        </button>
        <span className='sheet-foot-sp' />
        <button
          type='button'
          className='btn'
          onClick={handleClearAll}
        >
          <i className='fa-solid fa-eraser' /> Clear all
        </button>
        {props.clickOnExport && (
          <button
            type='button'
            className='btn'
            onClick={() => props.clickOnExport()}
          >
            <i className='fa-solid fa-paper-plane' /> Export
          </button>
        )}
        <button
          type='submit'
          className='btn primary'
          disabled={!formValid}
        >
          <i className={sp.startSearchButtonIconName || 'fa-solid fa-search'} /> {sp.startSearchButtonLabel || 'Search'}
        </button>
      </footer>
    );
  };

  const renderTabs = () => {
    const tabs = sp.tabsOnTheSearchPanel;
    if (!tabs) return null;
    return (
      <div className='seg filter-tabs'>
        {tabs.map((tab, index) => (
          <button
            type='button'
            key={`tabkey${index}`}
            className={selectedTab === index ? 'on' : ''}
            onClick={() => setSelectedTab(index)}
          >
            {tab.icon && <i className={tab.icon} />} {tab.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className='bravo-ds'>
        <div
          className='scrim'
          onClick={() => props.clickOnCancel && props.clickOnCancel()}
        />
        <aside
          className='sheet'
          role='dialog'
          aria-modal='true'
        >
          <header>
            <h2>Filters</h2>
            <button
              type='button'
              className='icon-btn'
              aria-label='Close'
              onClick={() => props.clickOnCancel && props.clickOnCancel()}
            >
              <i className='fa-solid fa-xmark' />
            </button>
          </header>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const tempSearchItem = { ...searchItem };
              tempSearchItem.SingleSearchLineText = '';
              if (props.enableUrlPush) {
                urlHelper.addSearchParamsToUrl(tempSearchItem, new sp(true));
              }
              props.clickOnOk(ShownPanelsEnum.Search, tempSearchItem);
              props.hideFilter();
            }}
          >
            <div className='body'>
              {renderTabs()}
              <div className='filter-fields'>
                {sp.fieldsInSearchBox.map((f) => {
                  if (sp.tabsOnTheSearchPanel) {
                    return !f.tab || f.tab === sp.tabsOnTheSearchPanel[selectedTab].id ? renderInputField(f) : null;
                  }
                  return renderInputField(f);
                })}
              </div>
            </div>
            {renderButtons()}
          </form>
        </aside>
      </div>
      {openSavePanel}
    </>
  );
}

GeneralSearchPanel.defaultProps = {
  searchItem: null,
  clickOnOk: (searchItem) => alert('Please, create an Ok function!'),
  clickOnCancel: (searchItem) => alert('Please, create a Cancel function!'),
  hideFilter: (searchItem) => alert('Please, create a Hide function!'),
  searchparamsType: null,
  exportFunction: null,
  enableUrlPush: true,
  clickOnClearAll: null,
  changeOfSearchItemFunction: null,
};
